import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, LayoutDashboard, Store } from "lucide-react";
import api from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

const STATUS_LABEL = {
  awaiting_payment_test: { ar: "بانتظار الدفع (تجريبي)", en: "Awaiting payment (test)" },
  completed: { ar: "مكتمل", en: "Completed" },
};

export default function Account() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (user?.role === "customer") api.get("/orders/mine").then((r) => setOrders(r.data)).catch(() => {});
  }, [user]);

  const doLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 md:py-12 fade-in">
      <div className="flex items-center justify-between mb-6 text-sm">
        <button onClick={() => nav(-1)} data-testid="account-back" className="text-text-muted hover:text-primary">← رجوع</button>
        <Link to="/" data-testid="account-home" className="text-text-muted hover:text-primary">العودة إلى الرئيسية</Link>
      </div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl">{user?.name}</h1>
          <p className="text-sm text-text-muted" dir="ltr">{user?.email}</p>
        </div>
        <button onClick={doLogout} data-testid="logout-btn" className="flex items-center gap-2 text-sm text-text-muted hover:text-destructive">
          <LogOut className="w-4 h-4" strokeWidth={1.5} /> {t("logout")}
        </button>
      </div>

      {user?.role === "owner" && (
        <Link to="/admin" className="flex items-center gap-2 border border-border px-5 py-3 mb-4 text-sm hover:bg-surface" data-testid="go-admin">
          <LayoutDashboard className="w-4 h-4" strokeWidth={1.5} /> {lang === "ar" ? "لوحة الإدارة" : "Admin dashboard"}
        </Link>
      )}
      {(user?.role === "seller" || user?.role === "owner") && (
        <Link to="/pos" className="flex items-center gap-2 border border-border px-5 py-3 mb-8 text-sm hover:bg-surface" data-testid="go-pos">
          <Store className="w-4 h-4" strokeWidth={1.5} /> {lang === "ar" ? "نقطة البيع (الكاشير)" : "POS (cashier)"}
        </Link>
      )}

      {user?.role === "customer" && (
        <>
          <h2 className="text-sm tracking-luxe uppercase text-accent mb-4">{t("myOrders")}</h2>
          {orders.length === 0 ? (
            <p className="text-text-muted text-sm">{lang === "ar" ? "لا توجد طلبات بعد" : "No orders yet"}</p>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} data-testid={`order-${o.id}`} className="border border-border p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-text-muted">طلب #{o.order_no} · {new Date(o.created_at).toLocaleDateString("ar-KW")}</span>
                    <span className="text-accent" data-testid={`order-status-${o.id}`}>{o.status_label || o.status}</span>
                  </div>
                  {o.items.map((it, i) => (
                    <div key={i} className="text-xs text-text-muted">
                      <div className="flex justify-between">
                        <span>{lang === "ar" ? it.name_ar : it.name_en || it.name_ar}{it.color ? ` · ${it.color_ar || it.color}` : ""}</span>
                        <span>{formatKWD(it.line_total, lang)}</span>
                      </div>
                      {it.measurements && <div className="text-[10px]">الطول {it.measurements.length} · العرض {it.measurements.width} · الصدر {it.measurements.chest} · الكتف {it.measurements.shoulder} · الكم {it.measurements.sleeve} · الخصر {it.measurements.waist} سم</div>}
                    </div>
                  ))}
                  <div className="flex justify-between text-xs mt-2 pt-2 border-t border-border text-text-muted">
                    <span>التوصيل</span><span>{formatKWD(o.delivery_fee || 0, lang)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t("total")}</span><span>{formatKWD(o.total, lang)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
